/**
 * Global fast tooltips.
 *
 * THE PROBLEM
 * -----------
 * Every icon button in this app labels itself with a plain `title` attribute
 * ("Attach Files", "Export", "New chat", ~200 of them). The browser decides
 * when to render that tooltip and the wait is roughly half a second to a
 * second — not configurable from CSS, JS or any browser API. On a toolbar of
 * unlabelled icons that delay is the whole affordance, arriving late.
 *
 * THE FIX
 * -------
 * One delegated listener on the document. On hover/focus it MOVES the `title`
 * off the element (into `data-fast-tip`) so the browser has nothing left to
 * render, and draws our own tooltip after SHOW_DELAY_MS. The title is put back
 * the moment the pointer leaves, so the DOM is unchanged at rest.
 *
 * Nothing at the call sites changes. No component imports this, no element
 * opts in — `title="..."` keeps working and simply gets fast.
 *
 * DOs
 * - Keep using `title="..."` in components. That is the whole API.
 * - `data-native-title` on an element (or any ancestor) to opt OUT and keep the
 *   browser's own tooltip.
 * - Tune the delay at RUNTIME with `window.ARTA_TOOLTIP_DELAY_MS = 150` — no
 *   rebuild, works on a deployed tenant from the console.
 *
 * DON'Ts
 * - Do NOT read `el.getAttribute("title")` in app code that can run under the
 *   cursor: while an element is hovered its title lives in `data-fast-tip`.
 *   Use `titleOf(el)` below if you need it.
 * - Do NOT give the tooltip node pointer events (see fastTooltip.css).
 * - Do NOT install this more than once; `installFastTooltips` is idempotent and
 *   returns the same uninstall function.
 *
 * ACCESSIBILITY
 * The title is only absent while the element is hovered or focused, and for
 * that window the tooltip is wired up with `aria-describedby`. Elements with no
 * other accessible name (icon-only buttons) also get a temporary `aria-label`,
 * so stripping the title never leaves a control nameless.
 */

import "./fastTooltip.css";

// Native is ~500-1000ms. 70ms reads as instant without firing on every pixel
// the cursor crosses on its way somewhere else.
const DEFAULT_SHOW_DELAY_MS = 70;

// Where a hovered element's title is parked. Also the marker that says "this
// element's title is currently ours" — release() keys off it.
const STASH_ATTR = "data-fast-tip";
const OPT_OUT_SELECTOR = "[data-native-title]";
const ARIA_LABEL_MARK = "data-fast-tip-aria";
const TIP_ID = "arta-fast-tip";

// `title` on these is metadata or an accessible name, never a hover tooltip —
// an <iframe title> names the frame and renders nothing. Stripping it would
// cost accessibility and buy no speed.
const SKIP_TAGS = new Set([
  "IFRAME",
  "FRAME",
  "LINK",
  "STYLE",
  "SCRIPT",
  "TRACK",
  "META",
  "BASE",
  "SOURCE",
  "PARAM",
  "TITLE",
]);

// Gap between the trigger's edge and the tip, and the minimum breathing room
// kept against the viewport edges when clamping.
const OFFSET_PX = 6;
const EDGE_PAD_PX = 4;


let installed = null;

/**
 * The title text of an element, wherever it currently lives.
 *
 * Call this instead of `getAttribute("title")` anywhere that might run while
 * the element is under the cursor.
 */
export function titleOf(el) {
  if (!el || typeof el.getAttribute !== "function") return "";
  return el.getAttribute("title") || el.getAttribute(STASH_ATTR) || "";
}

function showDelayMs() {
  // Runtime window override wins so the delay is tunable on a deployed tenant
  // without a rebuild. The build-time env var is the fallback, and it is
  // deliberately allowed to be unset (see the FE build portability note: one
  // artifact has to serve every domain).
  const runtime = Number(
    typeof window !== "undefined" ? window.ARTA_TOOLTIP_DELAY_MS : NaN
  );
  if (Number.isFinite(runtime) && runtime >= 0) return runtime;
  const built = Number(process.env.REACT_APP_TOOLTIP_DELAY_MS);
  if (Number.isFinite(built) && built >= 0) return built;
  return DEFAULT_SHOW_DELAY_MS;
}

/**
 * Install the global handler. Safe to call repeatedly; returns the uninstall.
 */
export function installFastTooltips() {
  if (installed) return installed;
  if (typeof document === "undefined") return () => {};

  const tip = document.createElement("div");
  tip.className = "arta-fast-tip";
  tip.id = TIP_ID;
  tip.setAttribute("role", "tooltip");
  tip.setAttribute("aria-hidden", "true");

  let activeEl = null;
  let showTimer = null;
  // Frame watchdog, live only while the tip is on screen. A trigger that React
  // unmounts mid-hover NEVER fires pointerout — a detached node's events do not
  // reach the document — so without this the tooltip is orphaned on screen with
  // nothing left to dismiss it. One isConnected check per frame, and only while
  // something is actually shown.
  let watchdog = null;
  // Watches the hovered element for React putting `title` back (it does that
  // when the prop's VALUE changes — a re-render alone leaves the stripped
  // attribute alone, because React diffs against its own previous props, not
  // against the DOM).
  const observer = new MutationObserver(() => {
    if (!activeEl) return;
    const restored = activeEl.getAttribute("title");
    if (!restored) return;
    activeEl.setAttribute(STASH_ATTR, restored);
    activeEl.removeAttribute("title");
    if (tip.dataset.show === "1") {
      tip.textContent = restored;
      position(activeEl);
    }
  });

  function candidateFrom(target) {
    if (!target || typeof target.closest !== "function") return null;
    const el = target.closest("[title]");
    if (!el) return null;
    if (SKIP_TAGS.has(el.tagName)) return null;
    if (el.closest(OPT_OUT_SELECTOR)) return null;
    const text = (el.getAttribute("title") || "").trim();
    return text ? el : null;
  }

  function position(el) {
    const r = el.getBoundingClientRect();
    const t = tip.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // ONE rule for every trigger, icon or panel: centred on the element, just
    // below it, flipping above only when there is no room below.
    //
    // It used to follow the cursor on wide elements, the way the native tooltip
    // does. That read as a bug: entering a source card near its top dropped the
    // tip INSIDE the card, entering the same card near its bottom dropped it
    // below — same card, same text, two positions, and in the first case it
    // covered the very thing being pointed at. Predictable beats clever here.
    let top = r.bottom + OFFSET_PX;
    if (top + t.height > vh - EDGE_PAD_PX && r.top - OFFSET_PX - t.height > EDGE_PAD_PX) {
      top = r.top - OFFSET_PX - t.height;
    }
    top = Math.max(EDGE_PAD_PX, Math.min(top, vh - t.height - EDGE_PAD_PX));

    let left = r.left + r.width / 2 - t.width / 2;
    left = Math.max(EDGE_PAD_PX, Math.min(left, vw - t.width - EDGE_PAD_PX));

    tip.style.transform = `translate3d(${Math.round(left)}px, ${Math.round(top)}px, 0)`;
  }

  function show() {
    showTimer = null;
    if (!activeEl || !activeEl.isConnected) return;
    const text = activeEl.getAttribute(STASH_ATTR);
    if (!text) return;

    tip.textContent = text;
    // A one-line label centres. Anything multi-line is a block — a name, a URL,
    // a hint — and centred prose with a wrapped URL in it is unreadable.
    tip.dataset.multiline = text.includes("\n") ? "1" : "0";
    tip.dataset.show = "1";
    tip.setAttribute("aria-hidden", "false");
    activeEl.setAttribute("aria-describedby", TIP_ID);
    // Icon-only control: with the title parked it has no accessible name at
    // all, so lend it one for the duration.
    const named =
      activeEl.getAttribute("aria-label") ||
      activeEl.getAttribute("aria-labelledby") ||
      (activeEl.textContent || "").trim();
    if (!named) {
      activeEl.setAttribute("aria-label", text);
      activeEl.setAttribute(ARIA_LABEL_MARK, "1");
    }
    // Measure first (the node is in the DOM and laid out), then place it.
    position(activeEl);
    startWatchdog();
  }

  function watch() {
    watchdog = null;
    if (!activeEl) return;
    if (!activeEl.isConnected) {
      release();
      return;
    }
    watchdog = window.requestAnimationFrame(watch);
  }

  function startWatchdog() {
    if (watchdog !== null) return;
    if (typeof window.requestAnimationFrame !== "function") return;
    watchdog = window.requestAnimationFrame(watch);
  }

  function capture(el) {
    if (el === activeEl) return;
    release();
    activeEl = el;
    el.setAttribute(STASH_ATTR, el.getAttribute("title"));
    el.removeAttribute("title");
    observer.observe(el, { attributes: true, attributeFilter: ["title"] });
    showTimer = window.setTimeout(show, showDelayMs());
  }

  function release() {
    if (showTimer !== null) {
      window.clearTimeout(showTimer);
      showTimer = null;
    }
    if (watchdog !== null) {
      window.cancelAnimationFrame(watchdog);
      watchdog = null;
    }
    tip.dataset.show = "0";
    tip.setAttribute("aria-hidden", "true");

    const el = activeEl;
    activeEl = null;
    if (!el) return;
    observer.disconnect();
    // An element unmounted mid-hover needs nothing put back.
    if (!el.isConnected) return;
    const text = el.getAttribute(STASH_ATTR);
    if (text) el.setAttribute("title", text);
    el.removeAttribute(STASH_ATTR);
    el.removeAttribute("aria-describedby");
    if (el.hasAttribute(ARIA_LABEL_MARK)) {
      el.removeAttribute("aria-label");
      el.removeAttribute(ARIA_LABEL_MARK);
    }
  }

  function onPointerOver(e) {
    // Touch has no hover: the tooltip would land on top of whatever the tap
    // opened. Leave those users the native long-press behaviour.
    if (e.pointerType === "touch") return;
    const el = candidateFrom(e.target);
    if (el) capture(el);
    else if (activeEl && !activeEl.contains(e.target)) release();
  }

  function onPointerOut(e) {
    if (!activeEl) return;
    // Moving between children of the same trigger is not leaving it.
    const to = e.relatedTarget;
    if (to && activeEl.contains(to)) return;
    release();
  }

  function onFocusIn(e) {
    const el = candidateFrom(e.target);
    if (el) capture(el);
    else if (activeEl) release();
  }

  function onKeyDown(e) {
    if (e.key === "Escape") release();
  }

  const hide = () => release();

  document.addEventListener("pointerover", onPointerOver, true);
  document.addEventListener("pointerout", onPointerOut, true);
  document.addEventListener("pointerdown", hide, true);
  document.addEventListener("focusin", onFocusIn, true);
  document.addEventListener("focusout", hide, true);
  document.addEventListener("keydown", onKeyDown, true);
  // Capture phase so a scroll inside any pane counts, not just the window's.
  window.addEventListener("scroll", hide, true);
  window.addEventListener("resize", hide);
  window.addEventListener("blur", hide);
  document.addEventListener("visibilitychange", hide);

  document.body.appendChild(tip);

  installed = function uninstallFastTooltips() {
    release();
    document.removeEventListener("pointerover", onPointerOver, true);
    document.removeEventListener("pointerout", onPointerOut, true);
    document.removeEventListener("pointerdown", hide, true);
    document.removeEventListener("focusin", onFocusIn, true);
    document.removeEventListener("focusout", hide, true);
    document.removeEventListener("keydown", onKeyDown, true);
    window.removeEventListener("scroll", hide, true);
    window.removeEventListener("resize", hide);
    window.removeEventListener("blur", hide);
    document.removeEventListener("visibilitychange", hide);
    observer.disconnect();
    if (tip.parentNode) tip.parentNode.removeChild(tip);
    installed = null;
  };
  return installed;
}

export default installFastTooltips;
